// src/SquadraModal.jsx
// Modal "Vedi squadra" — mostra il team di un'edizione CV
// Chiamata: GET /api/cv/editions/:editionId/team (JWT incluso tramite fetchJson)
import React, { useEffect, useState } from 'react';
import { fetchJson } from './api';
import { useTranslation } from 'react-i18next';

// ─── Palette (identica a MieiCreditiPage) ────────────────────────────────────
const GOLD        = '#d4a550';
const GOLD_DIM    = 'rgba(212,165,80,0.13)';
const GOLD_BORDER = 'rgba(212,165,80,0.28)';
const BORDER      = 'rgba(255,255,255,0.07)';
const TEXT        = '#e8e6df';
const TEXT_DIM    = 'rgba(232,230,223,0.5)';
const TEXT_MED    = 'rgba(232,230,223,0.78)';
const BG2         = '#13131f';
const BG3         = '#1a1a28';
const RED_DIM     = 'rgba(248,113,113,0.13)';
const RED         = '#f87171';

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Colore di sfondo per avatar fallback in base alle iniziali (deterministico)
function avatarColor(name = '') {
  const colors = [
    'rgba(212,165,80,0.35)',
    'rgba(96,165,250,0.35)',
    'rgba(74,222,128,0.35)',
    'rgba(167,139,250,0.35)',
    'rgba(248,113,113,0.35)',
    'rgba(251,191,36,0.35)',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

function initials(nome, cognome) {
  const a = (nome || '').trim()[0] || '';
  const b = (cognome || '').trim()[0] || '';
  return (a + b).toUpperCase() || '?';
}

// ─── AvatarFallback ───────────────────────────────────────────────────────────
function MemberAvatar({ nome, cognome, profile_image_url }) {
  const [err, setErr] = useState(false);
  const name = `${nome || ''} ${cognome || ''}`.trim();
  const ini  = initials(nome, cognome);

  if (profile_image_url && !err) {
    return (
      <img
        src={profile_image_url}
        alt={name}
        onError={() => setErr(true)}
        style={{
          width: 40, height: 40, borderRadius: '50%',
          objectFit: 'cover', flexShrink: 0,
          border: `1px solid ${BORDER}`,
        }}
      />
    );
  }
  return (
    <div style={{
      width: 40, height: 40, borderRadius: '50%',
      background: avatarColor(name),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 14, fontWeight: 700, color: TEXT,
      flexShrink: 0, border: `1px solid ${BORDER}`,
    }}>
      {ini}
    </div>
  );
}

// ─── Badge verifica ───────────────────────────────────────────────────────────
function VerifiedBadge({ roleVerifiedBy, t }) {
  if (roleVerifiedBy === 'production') {
    return (
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 8px',
        borderRadius: 20, background: GOLD_DIM,
        border: `1px solid ${GOLD_BORDER}`, color: GOLD,
        whiteSpace: 'nowrap', letterSpacing: '0.04em',
      }}>
        {t('crediti.verified_production')}
      </span>
    );
  }
  if (roleVerifiedBy === 'coordinator') {
    return (
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 8px',
        borderRadius: 20,
        background: 'rgba(148,163,184,0.12)',
        border: '1px solid rgba(148,163,184,0.3)',
        color: '#94a3b8',
        whiteSpace: 'nowrap', letterSpacing: '0.04em',
      }}>
        {t('crediti.verified_coordinator')}
      </span>
    );
  }
  return null;
}

// ─── Componente principale ────────────────────────────────────────────────────
export default function SquadraModal({ open, onClose, editionId, programName, editionLabel }) {
  const { t } = useTranslation();

  const [team, setTeam]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // Carica il team all'apertura del modal (o quando cambia editionId)
  useEffect(() => {
    if (!open || !editionId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setTeam([]);

    fetchJson(`/cv/editions/${editionId}/team`)
      .then(data => {
        if (!alive) return;
        setTeam(Array.isArray(data?.team) ? data.team : []);
      })
      .catch(() => {
        if (!alive) return;
        setError(t('crediti.modal_error'));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => { alive = false; };
  }, [open, editionId, t]);

  // Chiudi con Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const title = programName
    ? `${programName}${editionLabel ? ` — ${editionLabel}` : ''}`
    : editionLabel || '';

  const sottotitolo = team.length === 1
    ? t('crediti.modal_sottotitolo_one')
    : t('crediti.modal_sottotitolo', { count: team.length });

  return (
    // Overlay
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1300,
        background: 'rgba(0,0,0,0.62)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      {/* Card modal */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 560,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          background: BG2,
          border: `1px solid ${BORDER}`,
          borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'flex-start', gap: 12,
          position: 'sticky', top: 0, background: BG2, zIndex: 1,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase', color: GOLD, marginBottom: 4,
            }}>
              {t('crediti.sezione_titolo')}
            </div>
            <div style={{
              fontSize: 16, fontWeight: 700, color: TEXT,
              lineHeight: 1.3, marginBottom: 4,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {title}
            </div>
            {!loading && !error && team.length > 0 && (
              <div style={{ fontSize: 12, color: TEXT_DIM }}>
                {sottotitolo}
              </div>
            )}
          </div>
          {/* Bottone chiudi */}
          <button
            onClick={onClose}
            aria-label={t('crediti.modal_chiudi')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: TEXT_DIM, fontSize: 20, lineHeight: 1, padding: '2px 4px',
              borderRadius: 6, flexShrink: 0,
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = TEXT; }}
            onMouseLeave={e => { e.currentTarget.style.color = TEXT_DIM; }}
          >
            ×
          </button>
        </div>

        {/* Corpo */}
        <div style={{ padding: '16px 24px 24px' }}>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', color: TEXT_DIM, padding: '32px 0', fontSize: 13 }}>
              {t('crediti.modal_loading')}
            </div>
          )}

          {/* Errore */}
          {!loading && error && (
            <div style={{
              background: RED_DIM, border: '1px solid rgba(248,113,113,0.3)',
              borderRadius: 10, padding: '14px 18px', color: RED, fontSize: 13,
            }}>
              {error}
            </div>
          )}

          {/* Lista team */}
          {!loading && !error && team.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {team.map((member) => (
                <div
                  key={member.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: BG3,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 10,
                    padding: '10px 14px',
                  }}
                >
                  <MemberAvatar
                    nome={member.nome}
                    cognome={member.cognome}
                    profile_image_url={member.profile_image_url}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: TEXT,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {[member.nome, member.cognome].filter(Boolean).join(' ') || '—'}
                    </div>
                    {member.role && (
                      <div style={{ fontSize: 11, color: TEXT_DIM, marginTop: 2 }}>
                        {member.role}
                      </div>
                    )}
                  </div>
                  <VerifiedBadge roleVerifiedBy={member.role_verified_by} t={t} />
                </div>
              ))}
            </div>
          )}

          {/* Stato quasi-vuoto: solo l'utente ha dichiarato questa edizione */}
          {!loading && !error && team.length === 1 && (
            <p style={{
              marginTop: 16,
              fontSize: 12,
              fontStyle: 'italic',
              color: TEXT_DIM,
              textAlign: 'center',
              lineHeight: 1.6,
            }}>
              {t('crediti.modal_solo_tu')}
            </p>
          )}

          {/* Team vuoto (0 membri — caso raro, per robustezza) */}
          {!loading && !error && team.length === 0 && (
            <div style={{ textAlign: 'center', color: TEXT_DIM, padding: '32px 0', fontSize: 13 }}>
              {t('crediti.nessun_credito')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
